import { useLocation } from 'react-router';
import { trpc } from '@/data-loaders';
import { Button } from '@curtin-dxp/curtin-ui';
import { Save } from 'lucide-react';
import { UpdatedSchoolParams } from '#server/models';
import { useAwmsStore } from '@/store';
import { SearchFilterState } from '@/store/types';
import { useAdminData, useHandleError, useHandleSuccess } from '@/hooks';
import { generateFilterOptions } from '@/lib/utils';
import { FILTERS } from '@/constants';
import { SearchFilterDropDown } from '@/components/Common/SearchFilterDropDown';
import { Loader } from '@/components/Common/Loader';

interface AdminHeaderProps {
  updatedSchoolParams: UpdatedSchoolParams;
  setUpdatedSchoolParams: (updatedSchoolParams: UpdatedSchoolParams) => void;
}

export const AdminHeader = ({ updatedSchoolParams, setUpdatedSchoolParams }: AdminHeaderProps) => {
  const location = useLocation();
  const isSchoolParamsScreen = location.pathname === '/admin/school-parameters';
  const isScreenWithNoYearDropdown = location.pathname === '/admin/data-load';
  const getAdminYears = useAwmsStore((state) => state.getYears);
  const activeYear = useAwmsStore((state) => state.activeYear);
  const setActiveYear = useAwmsStore((state) => state.setActiveYear);
  const selectedSchoolId = useAwmsStore((state) => state.selectedSchoolId);
  const setSelectedSchoolId = useAwmsStore((state) => state.setSelectedSchoolId);
  const searchFilters: SearchFilterState = useAwmsStore((state) => state.searchFilters);
  const { schoolList } = useAdminData();
  const handleError = useHandleError();
  const trpcUtils = trpc.useUtils();
  const sendToastUpdateSchoolParametersSuccess = useHandleSuccess({ title: 'Update school parameters' });

  const filterOptions = [
    {
      placeholder: 'Select school',
      options: generateFilterOptions(schoolList),
      stateFilterFieldName: FILTERS.SCHOOL,
      selectedState: selectedSchoolId,
      setSelectedState: setSelectedSchoolId,
    },
    {
      placeholder: 'Select year',
      options: getAdminYears().map((year) => {
        return {
          label: year,
          value: parseInt(year),
        };
      }),
      stateFilterFieldName: FILTERS.YEAR,
      selectedState: activeYear,
      setSelectedState: setActiveYear,
      className: 'w-[125px]',
    },
  ];

  const handleInvalidateAndRefetch = async () => {
    await trpcUtils.admin.getSchoolParameters.invalidate({ schoolId: selectedSchoolId, year: activeYear });
    await trpcUtils.admin.getSchoolParameters.refetch({ schoolId: selectedSchoolId, year: activeYear });
  };

  const { isLoading: isUpdating, mutate: updateSchoolParameters } = trpc.admin.updateSchoolParameters.useMutation({
    onSuccess: async () => {
      setUpdatedSchoolParams({});
      sendToastUpdateSchoolParametersSuccess();
      await handleInvalidateAndRefetch();
    },
    onError: (error) => {
      setUpdatedSchoolParams({});
      handleError([error]);
    },
  });
  return (
    <>
      {isUpdating && <Loader />}
      <div className="flex w-screen flex-wrap items-center gap-8 border-b-[1px] border-grey-10 px-8 py-5 min-[1236px]:flex-row">
        <div className="relative flex items-center">
          <div className="pr-10 text-sh1 leading-[42px] text-primary-black-90">Admin view</div>
        </div>
        {filterOptions &&
          filterOptions.map((filter) => {
            if (!isSchoolParamsScreen && filter.stateFilterFieldName === FILTERS.SCHOOL) return;
            if (isScreenWithNoYearDropdown && filter.stateFilterFieldName === FILTERS.YEAR) return;
            const filterValue = searchFilters[filter.stateFilterFieldName];
            return (
              <SearchFilterDropDown
                key={filter.stateFilterFieldName}
                placeholder={filter.placeholder}
                options={filter.options}
                stateFilterFieldName={filter.stateFilterFieldName}
                stateFilterValue={filterValue}
                selectedState={filter.selectedState}
                setSelectedState={filter.setSelectedState}
                className={filter.className}
              />
            );
          })}
        {isSchoolParamsScreen && (
          <div className="ml-auto flex items-center">
            <Button
              variant="outline-secondary"
              className="h-10 border-black bg-black text-white hover:bg-transparent hover:text-black focus:outline-none focus-visible:ring-1
                focus-visible:ring-inset focus-visible:ring-white focus-visible:ring-offset-0"
              aria-label="Admin View"
              onClick={() =>
                updateSchoolParameters({
                  schoolId: selectedSchoolId,
                  updatedSchoolParams: updatedSchoolParams,
                  year: activeYear,
                })
              }
            >
              Save
              <Save className="ml-[10px] h-6 w-6" />
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
